<script setup>
/**
 * The table layout's header.
 *
 * The conversation list column - which owns the title, the display menu and the
 * filters - is hidden under this layout, so the table gets the same controls
 * here: the view title and layout controls on the first row, search and filters
 * on the second, the row count on the third.
 *
 * Nothing about filtering is re-implemented: the panels are the list's own
 * components, and applying, clearing and the display menu go back to the list's
 * handlers over the bus, so both layouts narrow the list the same way.
 */
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useDropdownPosition } from 'dashboard/composables/useDropdownPosition';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useConversationFilterContext } from 'dashboard/components-next/filter/provider.js';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator.js';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import ConversationBasicFilter from './ConversationBasicFilter.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  title: { type: String, required: true },
  // 'tickets' is the tickets screen, 'conversations' the inbox grid - the two
  // read the same controls with their own copy.
  variant: {
    type: String,
    default: 'tickets',
    validator: value => ['tickets', 'conversations'].includes(value),
  },
  showCreate: { type: Boolean, default: false },
  createLabel: { type: String, default: '' },
});

const emit = defineEmits(['create']);

const search = defineModel('search', { type: String, default: '' });

const { t } = useI18n();
const store = useStore();
const { filterTypes } = useConversationFilterContext();

const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const hasAppliedFilters = computed(() => appliedFilters.value.length > 0);

const copy = computed(() =>
  props.variant === 'conversations' ? 'CONVERSATION.TABLE' : 'TICKETS.TABLE'
);

// ---------------------- The filter panel ----------------------

const showFilterPanel = ref(false);
const showSaveViewPanel = ref(false);
const editableFilters = ref([]);

const filterTriggerRef = ref(null);
const filterPanelRef = ref(null);
const saveViewTriggerRef = ref(null);
const saveViewPanelRef = ref(null);

const { fixedPosition: filterPosition, updatePosition: updateFilterPosition } =
  useDropdownPosition(filterTriggerRef, filterPanelRef, showFilterPanel);

const {
  fixedPosition: saveViewPosition,
  updatePosition: updateSaveViewPosition,
} = useDropdownPosition(
  saveViewTriggerRef,
  saveViewPanelRef,
  showSaveViewPanel
);

watch([showFilterPanel, showSaveViewPanel], async () => {
  await nextTick();
  updateFilterPosition();
  updateSaveViewPosition();
});

const toggleFilterPanel = () => {
  if (showFilterPanel.value) {
    showFilterPanel.value = false;
    return;
  }
  // The panel renders one row per filter, so it opens on the applied ones -
  // or on an empty condition, which is what there is to edit when none are.
  editableFilters.value = appliedFilters.value.length
    ? appliedFilters.value.map(filter => ({ ...filter }))
    : [
        {
          attributeKey: 'status',
          filterOperator: 'equal_to',
          values: [],
          queryOperator: 'and',
        },
      ];
  showFilterPanel.value = true;
};

// The list owns the fetch, so applying is its handler running on the filters
// the panel just wrote to the store.
const applyFilters = filters => {
  emitter.emit(BUS_EVENTS.TABLE_APPLY_FILTERS, filters);
  showFilterPanel.value = false;
};

const clearFilters = () => {
  emitter.emit(BUS_EVENTS.TABLE_RESET_FILTERS);
  showFilterPanel.value = false;
};

const removeFilter = index => {
  const remaining = appliedFilters.value.filter(
    (filter, filterIndex) => filterIndex !== index
  );
  if (!remaining.length) {
    clearFilters();
    return;
  }
  store.dispatch('setConversationFilters', useSnakeCase(remaining));
  applyFilters(remaining);
};

const onBasicFilterChange = (value, type) =>
  emitter.emit(BUS_EVENTS.TABLE_BASIC_FILTER_CHANGE, { value, type });

// ---------------------- The applied filter chips ----------------------

const filterTypeFor = attributeKey =>
  filterTypes.value.find(type => type.attributeKey === attributeKey);

const valueLabel = (attributeKey, value) => {
  if (value?.name) return value.name;
  const option = filterTypeFor(attributeKey)?.options?.find(
    ({ id }) => String(id) === String(value?.id ?? value)
  );
  return option?.name ?? String(value?.id ?? value);
};

const filterChips = computed(() =>
  appliedFilters.value.map((filter, index) => {
    const values = (filter.values ?? []).map(value =>
      valueLabel(filter.attributeKey, value)
    );
    const name =
      filterTypeFor(filter.attributeKey)?.attributeName ?? filter.attributeKey;
    return {
      index,
      label: values.length ? `${name}: ${values.join(', ')}` : name,
    };
  })
);

// ---------------------- Saving the filters as a view ----------------------

// Ticket views are conversation views carrying a `tickets` context, which is
// what keeps them out of the chat folder lists.
const savedViewQuery = computed(() => {
  const query = filterQueryGenerator(useSnakeCase(appliedFilters.value));
  return props.variant === 'tickets' ? { ...query, context: 'tickets' } : query;
});
</script>

<template>
  <div class="flex flex-col gap-2 mb-2">
    <div class="flex items-start justify-between gap-2">
      <div class="min-w-0">
        <h1 class="m-0 text-lg font-semibold truncate text-n-slate-12">
          {{ title }}
        </h1>
        <p class="m-0 text-sm text-n-slate-10">
          {{ t(`${copy}.TOOLBAR.SUBTITLE`) }}
        </p>
      </div>
      <NextButton
        v-if="showCreate"
        data-testid="new-ticket-trigger"
        icon="i-lucide-plus"
        sm
        :label="createLabel"
        @click="emit('create')"
      />
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <div class="relative w-64">
        <span
          class="absolute -translate-y-1/2 pointer-events-none i-lucide-search size-3.5 text-n-slate-10 top-1/2 start-2.5"
        />
        <input
          v-model="search"
          type="search"
          data-testid="table-search"
          :placeholder="t(`${copy}.TOOLBAR.SEARCH_PLACEHOLDER`)"
          :aria-label="t(`${copy}.TOOLBAR.SEARCH_PLACEHOLDER`)"
          class="reset-base w-full h-8 py-0 text-sm border rounded-lg ps-8 pe-3 mb-0 border-n-weak bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-brand"
        />
      </div>
      <div ref="filterTriggerRef" class="relative">
        <NextButton
          id="toggleConversationFilterButton"
          data-testid="table-filter-trigger"
          icon="i-lucide-list-filter"
          :label="t(`${copy}.TOOLBAR.FILTER`)"
          sm
          faded
          slate
          @click="toggleFilterPanel"
        />
      </div>
      <template v-if="hasAppliedFilters">
        <button
          v-for="chip in filterChips"
          :key="chip.index"
          type="button"
          data-testid="table-filter-chip"
          class="flex items-center h-7 gap-1 rounded-full bg-n-violet-3 px-2.5 text-xs font-medium text-n-violet-11 hover:bg-n-violet-4"
          @click="removeFilter(chip.index)"
        >
          <span class="truncate max-w-48">{{ chip.label }}</span>
          <span class="i-lucide-x size-3 shrink-0" />
        </button>
        <NextButton
          data-testid="table-clear-filters"
          :label="t(`${copy}.TOOLBAR.CLEAR_ALL`)"
          sm
          ghost
          slate
          @click="clearFilters"
        />
        <div ref="saveViewTriggerRef" class="relative">
          <NextButton
            data-testid="table-save-view"
            icon="i-lucide-save"
            :label="t(`${copy}.TOOLBAR.SAVE_VIEW`)"
            sm
            ghost
            slate
            @click="showSaveViewPanel = !showSaveViewPanel"
          />
        </div>
      </template>
      <div class="flex items-center gap-1 ltr:ml-auto rtl:mr-auto shrink-0">
        <ConversationBasicFilter @change-filter="onBasicFilterChange" />
      </div>
    </div>

    <TeleportWithDirection>
      <div
        v-if="showFilterPanel"
        ref="filterPanelRef"
        :class="filterPosition.class"
        :style="filterPosition.style"
      >
        <ConversationFilter
          v-model="editableFilters"
          @apply-filter="applyFilters"
          @close="showFilterPanel = false"
        />
      </div>
      <div
        v-if="showSaveViewPanel"
        ref="saveViewPanelRef"
        :class="saveViewPosition.class"
        :style="saveViewPosition.style"
      >
        <SaveCustomView
          v-model="editableFilters"
          :custom-views-query="savedViewQuery"
          @close="showSaveViewPanel = false"
        />
      </div>
    </TeleportWithDirection>
  </div>
</template>
